import React, { useState } from 'react';
import { ArrowRight, Gift, Truck } from 'lucide-react';
import { buyNow } from '../../utils/checkout';
import { formatPrice } from '../../utils/currency';
import type { Bundle } from '../../types/blackout';
interface BundleCardProps {
  bundle: Bundle;
  selected: boolean;
  showBonus: boolean;
  onSelect: () => void;
}
const PACK_OPTIONS = [1, 2, 3, 4];
export function BundleCard({
  bundle,
  selected,
  showBonus,
  onSelect
}: BundleCardProps) {
  const [packs, setPacks] = useState(1);
  const savings = bundle.regularPrice - bundle.price;
  const perMask = bundle.price / bundle.count;
  const isSingle = bundle.count === 1;
  const links = bundle.quantityLinks;
  const buyUrl = links?.[packs];
  return <div className={`relative flex h-full flex-col transition-[border-color,background-color] duration-150 ${selected ? 'border-2 border-bone bg-carbon' : isSingle ? 'border border-gunmetal/70 hover:border-steel' : 'border border-gunmetal bg-carbon/60 hover:border-steel'} ${isSingle ? 'order-last md:order-none' : ''}`} data-id="element-55" data-mp="6z8cy2cf3n4i">
      <button type="button" role="radio" aria-checked={selected} onClick={onSelect} className="flex flex-1 flex-col p-4 text-left md:p-5" data-id="element-56" data-mp="30irn4kf2qgz">
        <div className="flex w-full items-center justify-between gap-3" data-id="element-57" data-mp="3pfamf34q3t3e">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-steel" data-id="element-58" data-mp="h00ng3w4r3zu">
            {bundle.number} //
            {bundle.badge && <span className={`ml-2 px-2 py-0.5 text-[10px] ${bundle.popular ? 'bg-bone text-obsidian' : 'border border-bone/40 text-bone'}`} data-id="element-59" data-mp="lylcd75xm18z">
                {bundle.badge}
              </span>}
          </span>
          <span aria-hidden="true" className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${selected ? 'border-bone' : 'border-gunmetal'}`} data-id="element-60" data-mp="2sorv5pr3m25q">
            {selected && <span className="h-2.5 w-2.5 rounded-full bg-bone" data-id="element-61" data-mp="y9cnvrs766kl" />}
          </span>
        </div>

        <h3 className={`mt-4 font-display uppercase leading-none ${isSingle ? 'text-2xl text-bone/85' : 'text-3xl text-bone'}`} data-id="element-62" data-mp="ybp5u4xyggxn">
          {bundle.name}
        </h3>
        <p className="mt-1.5 text-[13px] font-semibold uppercase tracking-[0.06em] text-bone/90" data-id="element-63" data-mp="khy0z9v65l89">
          {bundle.deal}
          {!isSingle && <span className="text-steel" data-id="element-64" data-mp="fhtjbgiiykgg"> · {bundle.count} masks</span>}
        </p>

        <div className="mt-4 flex items-baseline gap-2.5" data-id="element-65" data-mp="aj8mzs6e8kxa">
          <span className={`font-display leading-none ${isSingle ? 'text-3xl text-bone/85' : 'text-4xl text-bone'}`} data-id="element-66" data-mp="allbypakjvju">
            {formatPrice(bundle.price)}
          </span>
          {savings > 0 && <span className="text-[15px] text-steel line-through" data-id="element-67" data-mp="1zyyatyzwwrdk">
              {formatPrice(bundle.regularPrice)}
            </span>}
        </div>
        <p className="mt-1.5 text-[13px] text-steel" data-id="element-68" data-mp="28fgsvgl8keg8">
          {formatPrice(perMask)} / mask
          {savings > 0 && <span className="font-semibold text-bone" data-id="element-69" data-mp="2h80rwi5fwl1l"> · Save {formatPrice(savings)}</span>}
        </p>

        <div className="mt-auto w-full space-y-1.5 pt-4 text-[12px]" data-id="element-70" data-mp="2m7n6aux087bz">
          <p className={`flex h-4 items-center gap-2 ${bundle.shipping === 'free' ? 'text-bone' : 'text-steel'}`} data-id="element-71" data-mp="2m8yagvud3h3m">
            <Truck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-72" data-mp="1tiye5ldxfccd" />
            {bundle.shipping === 'free' ? 'Free U.S. shipping' : 'Shipping applies'}
          </p>
          <p className="flex h-4 items-center gap-2 text-bone/85" data-id="element-73" data-mp="23gw0g3c86z2c">
            {showBonus && bundle.bonus && <>
                <Gift className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-74" data-mp="39w6c93sl080w" />
                Bonus: {bundle.bonus}
              </>}
          </p>
        </div>
      </button>

      {/* Quantity + Buy Now — only for bundles that have buy-now links */}
      {links && <div className="border-t border-gunmetal p-4 md:p-5" data-id="element-75" data-mp="mpabzhs07i3l">
          <p id={`${bundle.id}-qty-label`} className="text-[11px] font-semibold uppercase tracking-[0.18em] text-steel" data-id="element-76" data-mp="10nrw75ipacgr">
            Quantity
          </p>
          <div role="radiogroup" aria-labelledby={`${bundle.id}-qty-label`} className="mt-2 grid grid-cols-4 gap-1.5" data-id="element-77" data-mp="1am7ohbtgrrru">
            {PACK_OPTIONS.map(qty => {
          const available = Boolean(links[qty]);
          const active = qty === packs;
          return <button key={qty} type="button" role="radio" aria-checked={active} disabled={!available} onClick={() => setPacks(qty)} title={available ? `${qty} × ${bundle.name}` : 'Coming soon'} className={`h-10 text-[13px] font-semibold transition-colors duration-150 disabled:cursor-not-allowed disabled:border-gunmetal/50 disabled:text-gunmetal ${active ? 'bg-bone text-obsidian' : 'border border-gunmetal text-bone hover:border-bone'}`} data-id="element-78" data-mp="1fco2oy53zhf9">
                  {qty}
                </button>;
        })}
          </div>
          <p className="mt-2 text-[12px] text-steel" data-id="element-79" data-mp="1tzd10wnmjok2">
            {packs} {packs === 1 ? 'pack' : 'packs'} · {bundle.count * packs} masks
          </p>

          <button type="button" disabled={!buyUrl} onClick={() => buyUrl && buyNow(bundle, packs, buyUrl)} className="group mt-3 inline-flex h-12 w-full items-center justify-center gap-2 bg-bone text-[13px] font-semibold uppercase tracking-[0.2em] text-obsidian transition-[background-color,transform] duration-150 ease-out hover:bg-white active:scale-[0.98] disabled:opacity-50" data-id="element-80" data-mp="1fcjtidkcjn2f">
            Buy now · {formatPrice(bundle.price * packs)}
            <ArrowRight className="h-4 w-4" aria-hidden="true" data-id="element-81" data-mp="3869nu33lp9nl" />
          </button>
        </div>}
    </div>;
}